import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";

/**
 * Hook to get user's current GPS location
 * Returns { lat, lng, loading, error }
 */
export function useUserLocation() {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneFallbacks = {
      "Asia/Manila": { lat: 14.5995, lng: 120.9842, source: "timezone", city: "Manila", country: "Philippines" },
      "Asia/Singapore": { lat: 1.3521, lng: 103.8198, source: "timezone", city: "Singapore", country: "Singapore" },
      "Asia/Dubai": { lat: 25.2048, lng: 55.2708, source: "timezone", city: "Dubai", country: "United Arab Emirates" },
      "Asia/Nicosia": { lat: 34.9182, lng: 33.6232, source: "timezone", city: "Larnaca coast", country: "Cyprus" },
      "Asia/Jerusalem": { lat: 32.0853, lng: 34.7818, source: "timezone", city: "Israel coast", country: "Israel" },
      "Europe/Athens": { lat: 37.9838, lng: 23.7275, source: "timezone", city: "Athens coast", country: "Greece" },
      "Europe/Rome": { lat: 41.9028, lng: 12.4964, source: "timezone", city: "Rome coast", country: "Italy" },
      "Europe/Madrid": { lat: 40.4168, lng: -3.7038, source: "timezone", city: "Spain", country: "Spain" },
    };
    const estimatedLocation = timezoneFallbacks[timezone] || { lat: 35.0, lng: 18.0, source: "estimate", city: "Mediterranean", country: "" };

    const reverseGeocode = async (lat, lng) => {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`);
      const data = await response.json();
      const address = data?.address || {};
      const city = address.city || address.town || address.village || address.municipality || address.county || address.state || "";
      return { city, country: address.country || "" };
    };

    const saveLocation = (nextLocation) => {
      if (cancelled) return;
      setLocation(nextLocation);
      base44.auth.updateMe({
        last_latitude: nextLocation.lat,
        last_longitude: nextLocation.lng,
        last_location_source: nextLocation.source,
        last_city: nextLocation.city || "",
        last_country: nextLocation.country || "",
        last_location_updated_at: new Date().toISOString(),
      }).catch(() => {});
      setError(null);
      setLoading(false);
    };

    const locateByIp = async (gpsError) => {
      const response = await base44.functions.invoke("detectUserGeo", { timezone });
      const geo = response?.data || {};
      if (geo.latitude && geo.longitude) {
        saveLocation({
          lat: geo.latitude,
          lng: geo.longitude,
          source: geo.source || "ip",
          city: geo.city || estimatedLocation.city || "",
          country: geo.countryName || estimatedLocation.country || "",
        });
        return;
      }
      setError(gpsError?.message || "Using estimated location");
      saveLocation(estimatedLocation);
    };

    if (!navigator.geolocation) {
      locateByIp(new Error("Geolocation not supported"));
      return () => { cancelled = true; };
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const gpsLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          source: "gps",
          city: "",
          country: "",
        };
        saveLocation(gpsLocation);
        reverseGeocode(gpsLocation.lat, gpsLocation.lng)
          .then((place) => saveLocation({ ...gpsLocation, city: place.city, country: place.country }))
          .catch(() => {});
      },
      (err) => locateByIp(err).catch(() => {
        setError(err.message);
        saveLocation(estimatedLocation);
      }),
      { enableHighAccuracy: false, timeout: 3500, maximumAge: 300000 }
    );

    return () => { cancelled = true; };
  }, []);

  return { location, loading, error };
}

/**
 * Calculate distance between two coordinates in km
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}